import { URL_PLATFORM_ADMIN_STAFF_USERS, URL_PLATFORM_ADMIN_TENANTS } from "store/services/URL";
import {
  fetchPlatformStaffUsers,
  fetchPlatformTenant,
  fetchPlatformTenants,
} from "store/services/platformAdmin.services";
import { buildStaffUsersQuery } from "../Staff/staffUsers.utils";
import { buildTenantsListQuery } from "../Tenants/tenantsList.utils";

export const AUDIT_PICKER_MIN_SEARCH = 2;

export const COMMON_AUDIT_ACTIONS = [
  "platform.ghost.start",
  "platform.usage.refresh",
  "platform.usage.refresh_all",
  "platform.tenant.suspend",
  "platform.tenant.activate",
  "platform.tenant.close_account",
  "platform.billing.cancel_immediate",
  "platform.billing.cancel_at_period_end",
  "platform.user.email_update",
  "platform.staff.deactivate",
];

export const mapTenantPickerOption = (tenant) => ({
  id: String(tenant.id),
  label: tenant.name || `Tenant #${tenant.id}`,
  description: [tenant.email, tenant.abn].filter(Boolean).join(" · ") || null,
});

export const mapStaffPickerOption = (staff) => ({
  id: String(staff.id),
  label: staff.name || staff.email || `Staff #${staff.id}`,
  description: staff.email || null,
});

export const searchAuditTenants = async (term) => {
  const endpoint = buildTenantsListQuery(
    {
      search: term,
      status: [],
      onTrial: false,
      readOnly: false,
      wildcard: false,
      careSector: "",
      page: 1,
      perPage: 10,
      sortField: "name",
      sortOrder: "asc",
    },
    URL_PLATFORM_ADMIN_TENANTS
  );
  const payload = await fetchPlatformTenants(endpoint);
  const rows = payload?.tenants?.data ?? payload?.tenants ?? [];

  return (Array.isArray(rows) ? rows : []).map(mapTenantPickerOption);
};

export const searchAuditStaff = async (term) => {
  const endpoint = buildStaffUsersQuery(
    {
      search: term,
      status: null,
      page: 1,
      perPage: 10,
      sortField: "name",
      sortOrder: "asc",
    },
    URL_PLATFORM_ADMIN_STAFF_USERS
  );
  const payload = await fetchPlatformStaffUsers(endpoint);
  const rows = payload?.staff_users?.data ?? payload?.staff_users ?? [];

  return (Array.isArray(rows) ? rows : []).map(mapStaffPickerOption);
};

export const resolveAuditTenantSelection = async (tenantId) => {
  if (!tenantId) {
    return null;
  }

  try {
    const tenant = await fetchPlatformTenant(tenantId);
    if (!tenant) {
      return { id: String(tenantId), label: `Tenant #${tenantId}` };
    }

    return mapTenantPickerOption(tenant);
  } catch {
    return { id: String(tenantId), label: `Tenant #${tenantId}` };
  }
};

export const resolveAuditStaffSelection = async (staffUserId) => {
  if (!staffUserId) {
    return null;
  }

  const endpoint = buildStaffUsersQuery(
    {
      search: "",
      status: null,
      page: 1,
      perPage: 100,
      sortField: "name",
      sortOrder: "asc",
    },
    URL_PLATFORM_ADMIN_STAFF_USERS
  );

  try {
    const payload = await fetchPlatformStaffUsers(endpoint);
    const rows = payload?.staff_users?.data ?? payload?.staff_users ?? [];
    const match = (Array.isArray(rows) ? rows : []).find(
      (row) => String(row.id) === String(staffUserId)
    );

    if (match) {
      return mapStaffPickerOption(match);
    }
  } catch {
    // fall through to placeholder label
  }

  return { id: String(staffUserId), label: `Staff user #${staffUserId}` };
};

export const parseAuditLogSearchParams = (searchParams) => ({
  spId: searchParams.get("sp_id") || "",
  adminUserId: searchParams.get("admin_user_id") || "",
  action: searchParams.get("action") || "",
});

export const syncAuditLogSearchParams = (searchParams, filters, actionInput) => {
  const next = new URLSearchParams(searchParams);

  if (filters.spId) {
    next.set("sp_id", String(filters.spId));
  } else {
    next.delete("sp_id");
  }

  if (filters.adminUserId) {
    next.set("admin_user_id", String(filters.adminUserId));
  } else {
    next.delete("admin_user_id");
  }

  const action = (actionInput || filters.action || "").trim();
  if (action) {
    next.set("action", action);
  } else {
    next.delete("action");
  }

  return next;
};
